import React from 'react';
import type { ReactNode } from 'react';
import clsx from 'clsx';
import { Colors, type ColorsType } from '@common/tokens';
import { Text } from '@common/components';

export type StatusPillVariantType =
  | 'inProgress'
  | 'warning'
  | 'danger'
  | 'success'
  | 'neutral'
  | 'timeOff';

type StatusPillPalette = { background: ColorsType; color: ColorsType };

const STATUS_PILL_PALETTE: Record<StatusPillVariantType, StatusPillPalette> = {
  inProgress: { background: 'skyLightest', color: 'textDefault' },
  warning: { background: 'sunLighter', color: 'textDefault' },
  danger: { background: 'coralHyperlight', color: 'textDefault' },
  success: { background: 'grassLightest', color: 'textDefault' },
  neutral: { background: 'steelLighter', color: 'textDefault' },
  timeOff: { background: 'violetLightest', color: 'violet' },
};

type StatusPillProps = {
  children: ReactNode;
  variant?: StatusPillVariantType;
  emphasized?: boolean;
  className?: string;
};

/**
 * Pill that surfaces an item’s status using semantic background colors from `@common/tokens`.
 *
 * **Variants (`StatusPillVariantType`):**
 * - `inProgress` — In a process; action needed.
 * - `warning` — Third party or waiting.
 * - `danger` — Negative / “sad” end; no go.
 * - `success` — Positive / “happy” end; no action needed.
 * - `neutral` — Completed or archived.
 * - `timeOff` — A non-worked block (PTO, leave) on a schedule.
 *
 * **`emphasized`:** bold uppercase label, for a category tag ("TIME OFF") rather than a status
 * sentence.
 *
 * **Label composition:** follow **“Status [State] [Newer State]”**, at most **28 characters**.
 * Length is not enforced at runtime; follow the contract when composing labels (including when
 * `children` is not a plain string).
 */
export function StatusPill({
  children,
  variant = 'inProgress',
  emphasized = false,
  className,
}: StatusPillProps) {
  const palette = STATUS_PILL_PALETTE[variant];

  return (
    <span className={clsx('wk-status-pill', className)}>
      <Text
        size="sm"
        color={palette.color}
        weight={emphasized ? 'bold' : 'light'}
        transform={emphasized ? 'uppercase' : undefined}
      >
        {children}
      </Text>
      <style jsx>{`
        .wk-status-pill {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 4px 8px;
          border-radius: 8px;
          background-color: ${Colors[palette.background]};
        }
      `}</style>
    </span>
  );
}
